class CarouselReel extends HTMLElement {
  constructor() {
    super();
    this.swiperWrapper = this.querySelector('.reel-slider');
    this.swiper = null;
  }

  loadSwiper() {
    if (!this.swiperPromise) {
      this.swiperPromise = new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
        script.onload = resolve;
        document.head.appendChild(script);
        const link = document.createElement('link');
        link.href = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';
        link.rel = 'stylesheet';
        document.head.appendChild(link);
      });
    }
    return this.swiperPromise;
  }

  addKeyPressListeners() {
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        this.swiper.slidePrev();
      } else if (event.key === 'ArrowRight') {
        this.swiper.slideNext();
      }
    });
  }

  async connectedCallback() {
    await this.loadSwiper();
    this.initCarousel();
    this.addKeyPressListeners();
  }

  initCarousel(attempt = 0) {
    const created = this.createCarousel();
    if (!created && attempt < 5) {
      requestAnimationFrame(() => this.initCarousel(attempt + 1));
    }
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.destroyCarousel();
      this.swiper = null;
    }
  }

  createCarousel() {
    if (this.querySelector('.reel-slider') == null) {
      this.swiperWrapper = this.children[0];
    } else {
      this.swiperWrapper = this.querySelector('.reel-slider');
    }

    let prevElWrapper = this.swiperWrapper.querySelector('.swiper-control--previous');
    let nextElWrapper = this.swiperWrapper.querySelector('.swiper-control--next');
    let prevEl = this.swiperWrapper.querySelector('.swiper-button-prev');
    let nextEl = this.swiperWrapper.querySelector('.swiper-button-next');

    //Grab elements via .children if rendered via React for Search tool
    if (prevElWrapper == null) {
      prevElWrapper = this.swiperWrapper.children[1];
    }

    if (nextElWrapper == null) {
      nextElWrapper = this.swiperWrapper.children[2];
    }

    if (prevEl == null) {
      prevEl = prevElWrapper.children[0];
    }

    if (nextEl == null) {
      nextEl = nextElWrapper.children[0];
    }

    if (!this.swiperWrapper) {
      console.error('CarouselReel: .reel-slider element not found');
      return false;
    }

    if (!prevElWrapper || !nextElWrapper || !prevEl || !nextEl) {
      console.error('CarouselReel: Navigation elements not found');
      return false;
    }

    if (this.swiper) {
      return true;
    }

    const rootStyles = getComputedStyle(document.documentElement);
    const spaceBetweenValue = rootStyles.getPropertyValue('--grids-standard-gutter').trim();
    const mobileSpaceBetweenValue = rootStyles.getPropertyValue('--layout-standard-mobile-gutter').trim();
    const pagePaddingValue = rootStyles.getPropertyValue('--grids-standard-margin').trim();
    const mobilePagePaddingValue = rootStyles.getPropertyValue('--layout-standard-mobile-margin').trim();
    const spaceBetween = parseFloat(spaceBetweenValue.replace('rem', '')) * 16;
    const mobileSpaceBetween = parseFloat(mobileSpaceBetweenValue.replace('rem', '')) * 16;
    const pagePadding = parseFloat(pagePaddingValue.replace('rem', '')) * 16;
    const mobilePagePadding = parseFloat(mobilePagePaddingValue.replace('rem', '')) * 16;

    const mobileSlidesPerView = parseFloat(this.getAttribute('mobileSlidesPerView')) || 1.25;

    this.swiper = new Swiper(this.swiperWrapper, {
      direction: 'horizontal',
      slidesOffsetAfter: pagePadding,
      loop: false,
      observer: true,
      observeParents: true,
      navigation: { nextEl, prevEl },
      breakpoints: {
        964: {
          spaceBetween,
          slidesPerView: 4.23,
        },
        300: {
          slidesOffsetAfter: mobilePagePadding,
          spaceBetween: mobileSpaceBetween,
          slidesPerView: mobileSlidesPerView,
        },
      },
      on: {
        init: () => {
          prevElWrapper.classList.add('swiper-control--hidden');
          prevEl.classList.add('swiper-button-hidden');
          prevEl.setAttribute('aria-hidden', true);
        },
        activeIndexChange: () => {
          // Previous button
          if (this.swiper && this.swiper.activeIndex !== 0) {
            prevEl.tabIndex = 0;
            prevElWrapper.classList.remove('swiper-control--hidden');
            prevEl.classList.remove('swiper-button-hidden');
            prevEl.setAttribute('aria-hidden', 'false');
            prevEl.setAttribute('aria-disabled', 'false');
            prevEl.removeAttribute('disabled');
          } else {
            prevEl.tabIndex = -1;
            prevElWrapper.classList.add('swiper-control--hidden');
            prevEl.classList.add('swiper-button-hidden');
            prevEl.setAttribute('aria-hidden', 'true');
            prevEl.setAttribute('aria-disabled', 'true');
            prevEl.setAttribute('disabled', 'true');
          }

          // Next button
          if (this.swiper && this.swiper.isEnd) {
            nextEl.tabIndex = -1;
            nextElWrapper.classList.add('swiper-control--hidden');
            nextEl.classList.add('swiper-button-hidden');
            nextEl.setAttribute('aria-hidden', 'true');
            nextEl.setAttribute('aria-disabled', 'true');
            nextEl.setAttribute('disabled', 'true');
          } else {
            nextEl.tabIndex = 0;
            nextElWrapper.classList.remove('swiper-control--hidden');
            nextEl.classList.remove('swiper-button-hidden');
            nextEl.setAttribute('aria-hidden', 'false');
            nextEl.setAttribute('aria-disabled', 'false');
            nextEl.removeAttribute('disabled');
          }
        },
        slideChange: (swiper) => {
          if (swiper.isEnd) {
            swiper.wrapperEl.classList.add('right-spacing');
          } else {
            swiper.wrapperEl.classList.remove('right-spacing');
          }

          // Liquid collection reels only: Bloomreach PDP uses a Preact pagination sentinel instead.
          const loadMoreSlide = this.swiperWrapper?.querySelector('.swiper-slide--load-more');
          if (!loadMoreSlide) return;

          const slidesPerView = typeof swiper.params.slidesPerView === 'number' ? swiper.params.slidesPerView : 1;
          const threshold = 10;
          if (swiper.slides.length - swiper.activeIndex <= slidesPerView + threshold) {
            this.loadMoreProducts();
          }
        },
        reachEnd: () => {
          const loadMoreSlide = this.swiperWrapper?.querySelector('.swiper-slide--load-more');
          if (!loadMoreSlide) return;
          this.loadMoreProducts();
        },
      },
    });

    return true;
  }

  async loadMoreProducts() {
    if (this.isLoadingMore) return;

    const loadMoreSlide = this.swiperWrapper.querySelector('.swiper-slide--load-more');
    if (!loadMoreSlide) return;

    const nextUrl = loadMoreSlide.getAttribute('data-next-url');
    if (!nextUrl) return;

    this.isLoadingMore = true;
    try {
      const response = await fetch(nextUrl);
      if (!response.ok) throw new Error('Network response was not ok');

      const htmlText = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlText, 'text/html');

      const newCarousel = doc.querySelector('carousel-reel');
      if (!newCarousel) return;

      const newSlides = Array.from(newCarousel.querySelectorAll('.swiper-slide.product-tile'));
      const newLoadMoreSlide = newCarousel.querySelector('.swiper-slide--load-more');

      if (this.swiper) {
        const loadMoreIndex = Array.from(this.swiper.slides).indexOf(loadMoreSlide);
        if (loadMoreIndex > -1) {
          this.swiper.removeSlide(loadMoreIndex);
        }

        newSlides.forEach((slide) => {
          this.swiper.appendSlide(slide);
        });

        if (newLoadMoreSlide) {
          this.swiper.appendSlide(newLoadMoreSlide);
        }

        this.swiper.update();
      }
    } catch (error) {
      console.error('Failed to load more products:', error);
    } finally {
      this.isLoadingMore = false;
    }
  }

  destroyCarousel() {
    this.swiper.destroy();
  }
}

customElements.define('carousel-reel', CarouselReel);
